import React, { useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { loginUser, mockUsers } from '../data/mockData';
import request from '../utils/request';
import CryptoJS from 'crypto-js';
import { initializeCart } from '../utils/cartManager';

interface LoginModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const LoginModal: React.FC<LoginModalProps> = ({ isOpen, onClose }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [isRegistering, setIsRegistering] = useState(false);
  const [showPasswordField, setShowPasswordField] = useState(false);
  const [marketingOptIn, setMarketingOptIn] = useState(true);
  const [isCheckingEmail, setIsCheckingEmail] = useState(false);
  const [isRegisteringLoading, setIsRegisteringLoading] = useState(false);
  const [registerError, setRegisterError] = useState('');
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  const [resetPasswordMessage, setResetPasswordMessage] = useState('');
  const [registerSuccessMessage, setRegisterSuccessMessage] = useState('');

  // Helper function to create MD5 hash
  const createMD5Hash = (password: string): string => {
    return CryptoJS.MD5(password).toString();
  };

  // Function to handle password reset
  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!forgotPasswordEmail.trim()) {
      setResetPasswordMessage('Please enter your email address');
      return;
    }

    setIsResettingPassword(true);
    setResetPasswordMessage('');

    try {
      // Here you would typically call an API endpoint to send reset email
      // For now, we'll simulate the API call
      const response = await request({
        method: 'POST',
        url: 'auth/forgot-password',
        data: { email: forgotPasswordEmail }
      });

      if (response.success) {
        setResetPasswordMessage('Password reset email sent! Please check your inbox.');
        setForgotPasswordEmail('');
        // Auto-hide the message after 5 seconds
        setTimeout(() => {
          setShowForgotPassword(false);
          setResetPasswordMessage('');
        }, 5000);
      } else {
        setResetPasswordMessage(response.message || 'Failed to send reset email. Please try again.');
      }
    } catch (error) {
      console.error('Password reset error:', error);
      setResetPasswordMessage('Failed to send reset email. Please try again.');
    } finally {
      setIsResettingPassword(false);
    }
  };

  // Function to check if email exists via API
  const checkEmailExists = async (email: string): Promise<boolean> => {
    try {
      const response = await request({
        method: 'POST',
        url: 'auth/check-email',
        data: { email }
      }); 
      if (response.success && response.result) {
        return response.result.exists;
      }
      return false
    } catch (error) {
      console.error('Error checking email:', error); 
    }
    return false
  };

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (email.trim()) {
      setIsCheckingEmail(true);
      try {
        console.log('email..............', email);
        const emailExists = await checkEmailExists(email);
        if (!emailExists) {
          setIsRegistering(true);
          setShowPasswordField(true);
        } else {
          setShowPasswordField(true);
        }
      } catch (error) {
        console.error('Error checking email:', error);
        // Default to showing password field if check fails
        setShowPasswordField(true);
      } finally {
        setIsCheckingEmail(false);
      }
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setRegisterError('');
    setIsRegisteringLoading(true);

    try {
      const username = firstName + lastName;
      const encryptedPassword = createMD5Hash(password);
      const response = await request({
        method: 'POST',
        url: 'auth/register',
        data: {
          email,
          password: encryptedPassword,
          firstName,
          lastName,
          username,
          marketingOptIn
        }
      });
      console.log('Registration response:', response);
      if (response.success && response.result) {
        console.log('Registration successful:', response.result);
        
        // Show success message
        setRegisterSuccessMessage('Registration successful! Please check your email for confirmation.');
        setTimeout(() => {
          setRegisterSuccessMessage('');
        }, 3000);

        // Store token if provided 
        if (response.result.token) {
          localStorage.setItem('auth_token', response.result.token);
          localStorage.setItem('currentUser', JSON.stringify(response.result.user));
        }
        // 切换回登录模式而不是关闭模态框
        setIsRegistering(false);
        setShowPasswordField(true);
        setPassword('');
        setFirstName('');
        setLastName('');
        setRegisterError('');
        // You can add a success callback here
      } else {
        setRegisterError(response.message || 'Registration failed');
      }
    } catch (error: any) {
      console.error('Registration error:', error);
      // enhancedRequest returns response.result directly, so error.message contains the API error message
      if (error.message) {
        setRegisterError(error.message);
      } else {
        setRegisterError('Registration failed. Please try again.');
      }
    } finally {
      setIsRegisteringLoading(false);
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginError('');
    setIsLoggingIn(true);

    try {
      const encryptedPassword = createMD5Hash(password);
      const response = await request({
        method: 'POST',
        url: 'auth/login',
        data: {
          email,
          password: encryptedPassword
        }
      }); 
      if (response.success && response.result) {
        console.log('Login successful:', response.result);
        // Store token if provided
        if (response.result.token) {
          localStorage.setItem('auth_token', response.result.token);
          localStorage.setItem('currentUser', JSON.stringify(response.result.user));
          
          // Initialize cart after successful login
          await initializeCart();
        }
        onClose();
        // You can add a success callback here
      } else {
        setLoginError(response.message || 'Login failed');
      }
    } catch (error: any) {
      console.error('Login error:', error);
      // enhancedRequest returns response.result directly, so error.message contains the API error message
      if (error.message) {
        setLoginError(error.message);
      } else {
        setLoginError('Login failed. Please try again.');
      }
    } finally {
      setIsLoggingIn(false);
    }
  };

  const handleBackToEmail = () => {
    setIsRegistering(false);
    setShowPasswordField(false);
    setPassword('');
    setFirstName('');
    setLastName('');
    setRegisterError('');
    setLoginError('');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      {registerSuccessMessage && (
        <div className="fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 flex items-center animate-fade-in-down">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          {registerSuccessMessage}
        </div>
      )}
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-900">
            {isRegistering ? 'Create your account' : showPasswordField ? 'Sign in' : 'Sign in'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {!showPasswordField ? (
            // Email Input Step
            <form onSubmit={handleEmailSubmit}>
              <div className="mb-4">
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  Email
                </label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                  placeholder="Enter your email"
                  required
                />
              </div>
              <button
                type="submit"
                disabled={isCheckingEmail}
                className="w-full bg-blue-800 text-white py-3 px-4 rounded-lg hover:bg-blue-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isCheckingEmail ? 'Checking...' : 'Continue'}
              </button>
            </form>
          ) : isRegistering ? (
            // Registration Form
            <form onSubmit={handleRegister}>
              <div className="mb-4">
                <p className="text-sm text-gray-600 mb-4">
                  Already have an account?{' '}
                  <button
                    type="button"
                    onClick={handleBackToEmail}
                    className="text-blue-600 hover:text-blue-700 font-medium"
                  >
                    Go back
                  </button>
                </p>
              </div>

              <div className="mb-4">
                <label htmlFor="reg-email" className="block text-sm font-medium text-gray-700 mb-2">
                  Email
                </label>
                <input
                  type="email"
                  id="reg-email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-3 py-2 border border-blue-600 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                  required
                />
              </div>

              <div className="mb-4 grid grid-cols-2 gap-3">
                <div>
                  <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-2">
                    First Name
                  </label>
                  <input
                    type="text"
                    id="firstName"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                    placeholder="First name"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-2">
                    Last Name
                  </label>
                  <input
                    type="text"
                    id="lastName"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                    placeholder="Last name"
                    required
                  />
                </div>
              </div>

              <div className="mb-6">
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                  Create Password
                </label>
                <div className="text-xs text-gray-500 mb-2">Must be at least 6 characters</div>
                <input
                  type="password"
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                  placeholder="Enter your password"
                  minLength={6}
                  required
                />
              </div>

              {/* <div className="mb-6">
                <label className="flex items-start space-x-3">
                  <input
                    type="checkbox"
                    checked={marketingOptIn}
                    onChange={(e) => setMarketingOptIn(e.target.checked)}
                    className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className="text-sm text-gray-700">
                    Yes! I'd like to receive exclusive GiftMkr deals, discounts and updates on new products.
                  </span>
                </label>
              </div>

              <div className="mb-6 text-sm text-gray-600">
                By clicking "Create new account" or "Continue," I agree to the{' '}
                <a href="/user-agreement" className="text-blue-600 hover:text-blue-700">
                  User Agreement
                </a>{' '}
                and{' '}
                <a href="/privacy-notice" className="text-blue-600 hover:text-blue-700">
                  Privacy Notice
                </a>
                .
              </div> */}

              {registerError && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-600">{registerError}</p>
                </div>
              )}

              <button
                type="submit"
                disabled={isRegisteringLoading}
                className="w-full bg-blue-800 text-white py-3 px-4 rounded-lg hover:bg-blue-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isRegisteringLoading ? 'Creating account...' : 'Create new account'}
              </button>
            </form>
          ) : (
            // Login Form
            <form onSubmit={handleLogin}>
              <div className="mb-4">
                <p className="text-sm text-gray-600 mb-4">
                  Welcome back! Please sign in to your account.
                </p>
              </div>

              <div className="mb-4">
                <label htmlFor="login-email" className="block text-sm font-medium text-gray-700 mb-2">
                  Email
                </label>
                <input
                  type="email"
                  id="login-email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                  required
                />
              </div>

              <div className="mb-6">
                <label htmlFor="login-password" className="block text-sm font-medium text-gray-700 mb-2">
                  Password
                </label>
                <input
                  type="password"
                  id="login-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                  placeholder="Enter your password"
                  required
                />
              </div>

              {loginError && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-600">{loginError}</p>
                </div>
              )}

              <button
                type="submit"
                disabled={isLoggingIn}
                className="w-full bg-blue-800 text-white py-3 px-4 rounded-lg hover:bg-blue-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoggingIn ? 'Signing in...' : 'Sign in'}
              </button>

              {/* Forgot Password Link */}
              <div className="text-center mt-3">
                <button
                  type="button"
                  onClick={() => setShowForgotPassword(true)}
                  className="text-sm text-blue-600 hover:text-blue-800 hover:underline transition-colors"
                >
                  Forgot your password?
                </button>
              </div>

              {/* Sign Up Button */}
              <button
                type="button"
                onClick={() => setIsRegistering(true)}
                className="w-full mt-3 bg-gray-100 text-gray-700 py-3 px-4 rounded-lg hover:bg-gray-200 transition-colors font-medium border border-gray-300"
              >
                Sign up
              </button>
            </form>
          )}

          {/* Social Login Options */}
          {!showPasswordField && (
            <div className="mt-6">
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-white text-gray-500">Or continue with</span>
                </div>
              </div>

              <div className="mt-6 grid grid-cols-3 gap-3">
                <button className="w-full inline-flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm bg-white text-sm font-medium text-gray-500 hover:bg-gray-50">
                  <svg className="w-5 h-5" viewBox="0 0 24 24">
                    <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                </button>
                <button className="w-full inline-flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm bg-white text-sm font-medium text-gray-500 hover:bg-gray-50">
                  <svg className="w-5 h-5" viewBox="0 0 24 24">
                    <path fill="currentColor" d="M12 2C6.477 2 2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.879V14.89h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.989C18.343 21.129 22 16.99 22 12c0-5.523-4.477-10-10-10z"/>
                  </svg>
                </button>
                <button className="w-full inline-flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm bg-white text-sm font-medium text-gray-500 hover:bg-gray-50">
                  <svg className="w-5 h-5" viewBox="0 0 24 24">
                    <path fill="currentColor" d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                  </svg>
                </button>
              </div>
            </div>
          )}

          {/* Forgot Password Form */}
          {showForgotPassword && (
            <div className="mt-6">
              <div className="text-center mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Reset Password</h3>
                <p className="text-sm text-gray-600 mt-1">Enter your email address and we'll send you a link to reset your password.</p>
              </div>

              <form onSubmit={handlePasswordReset} className="space-y-4">
                <div>
                  <label htmlFor="reset-email" className="block text-sm font-medium text-gray-700 mb-1">
                    Email Address
                  </label>
                  <input
                    id="reset-email"
                    type="email"
                    value={forgotPasswordEmail}
                    onChange={(e) => setForgotPasswordEmail(e.target.value)}
                    placeholder="Enter your email address"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>

                {resetPasswordMessage && (
                  <div className={`p-3 rounded-lg text-sm ${
                    resetPasswordMessage.includes('sent') 
                      ? 'bg-green-50 border border-green-200 text-green-600' 
                      : 'bg-red-50 border border-red-200 text-red-600'
                  }`}>
                    {resetPasswordMessage}
                  </div>
                )}

                <div className="flex space-x-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowForgotPassword(false);
                      setForgotPasswordEmail('');
                      setResetPasswordMessage('');
                    }}
                    className="flex-1 px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                    disabled={isResettingPassword}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isResettingPassword}
                    className="flex-1 bg-blue-800 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isResettingPassword ? 'Sending...' : 'Send Reset Link'}
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LoginModal; 